import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const VerifyAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, user, role, loading } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('Welcome!');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) return;

    // Prevent duplicate verification attempts
    if (verificationAttempted.current) return;

    const token = searchParams.get('token');

    // If already authenticated as a customer, just redirect (no verification needed)
    if (isAuthenticated && role === 'customer') {
      setWelcomeMessage('Welcome Back!');
      setStatus('success');
      const redirectPath = user?.profile_completed === false 
        ? '/complete-profile' 
        : '/customer/dashboard';
      setTimeout(() => navigate(redirectPath, { replace: true }), 1200);
      return;
    }

    // No token provided
    if (!token) {
      setStatus('error');
      setError('No verification link provided');
      return;
    }

    // Mark that we're attempting verification
    verificationAttempted.current = true;
    verifyToken(token);
  }, [loading, isAuthenticated, role, searchParams]);

  const verifyToken = async (token) => {
    try {
      const response = await authAPI.verifyMagicLink(token);
      const userData = response.data.user;
      
      // Login the user
      login(response.data.token, userData, response.data.role);
      
      // Determine welcome message and redirect
      const isNewUser = userData.profile_completed === false;
      setWelcomeMessage(isNewUser ? 'Welcome!' : 'Welcome Back!');
      setStatus('success');
      
      const redirectPath = isNewUser ? '/complete-profile' : '/customer/dashboard';
      setTimeout(() => navigate(redirectPath, { replace: true }), 1200);
      
    } catch (err) {
      // Token verification failed - show appropriate error
      setStatus('error');
      const errorMessage = err.response?.data?.detail || 'Verification failed';
      
      if (errorMessage.includes('expired') || errorMessage.includes('Invalid')) {
        setError('This link has expired or already been used. Please request a new one.');
      } else {
        setError(errorMessage);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFF0] flex items-center justify-center px-6">
      <div className="glass rounded-2xl p-10 max-w-md w-full text-center animate-scaleIn">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-[#C9A689] animate-spin mx-auto mb-6" />
            <h2 className="font-serif text-2xl text-[#4A154B] mb-3">Signing you in...</h2>
            <p className="text-gray-600">Please wait a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#8FBC8F]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#8FBC8F]" />
            </div>
            <h2 className="font-serif text-2xl text-[#4A154B] mb-3">{welcomeMessage}</h2>
            <p className="text-gray-600">Redirecting you now...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#C74545]/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-[#C74545]" />
            </div>
            <h2 className="font-serif text-2xl text-[#4A154B] mb-3">Link Expired</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/auth')}
              className="btn-primary"
              data-testid="request-new-link-btn"
            >
              Request New Link
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyAuth;
